import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ScrollText } from 'lucide-react'
import { api } from '../lib/api'
import { useResource } from '@retink/web-ui'
import { Badge } from '@retink/web-ui'
import { Button } from '@retink/web-ui'
import { Card, CardHeader, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@retink/web-ui'
import { PageHeader } from '@retink/web-ui'
import { EmptyState, TableEmpty } from '@retink/web-ui'
import { LoadingState, ErrorState } from '@retink/web-ui'

// The server caps a single audit query at 500 rows; we page up to that in steps.
const PAGE = 100
const MAX = 500

interface AuditEntry {
  id: string; occurred_at: string; action: string
  actor_email: string | null; actor_name: string | null
  tenant_id: string | null; actor_type: string
  target_type: string | null; target_id: string | null
  payload: Record<string, unknown> | null
}

const actorTone = (t: string) =>
  t === 'user' ? 'primary' : t === 'system' ? 'neutral' : 'outline'

export function Audit() {
  const [limit, setLimit] = useState(PAGE)
  const { data: entries = [], loading, error, reload } = useResource(
    () => api.get<AuditEntry[]>(`/api/admin/audit?limit=${limit}`), [limit],
  )

  const empty = !loading && !error && entries.length === 0
  // A full page back means there are probably more (until we hit the server cap).
  const maybeMore = entries.length >= limit && limit < MAX

  return (
    <div className="p-8">
      <PageHeader
        title="Activity"
        description={`Recent platform events — showing up to ${limit}${limit >= MAX ? ' (max per query)' : ''}.`}
        action={<Link to="/operations/services" className="text-sm text-primary hover:underline">View services</Link>}
      />

      <Card className="overflow-hidden">
        <CardHeader>Events</CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {['When', 'Action', 'Actor', 'Type', 'Target'].map((h) => (
                <TableHead key={h}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && entries.length === 0 && <TableEmpty colSpan={5}><LoadingState /></TableEmpty>}
            {!loading && error && entries.length === 0 && <TableEmpty colSpan={5}><ErrorState message={error} onRetry={reload} /></TableEmpty>}
            {empty && (
              <TableEmpty colSpan={5}>
                <EmptyState
                  icon={ScrollText}
                  title="No audit events yet"
                  description="Platform actions will appear here as they happen."
                />
              </TableEmpty>
            )}
            {entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {new Date(e.occurred_at).toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-xs text-foreground">{e.action}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.actor_email ?? '—'}</TableCell>
                <TableCell>
                  <Badge tone={actorTone(e.actor_type)}>{e.actor_type}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {e.target_type ?? '—'}
                  {e.target_id && <span className="ml-1 font-mono">{e.target_id}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {(maybeMore || limit >= MAX) && entries.length > 0 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground">
          {maybeMore ? (
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => setLimit((l) => Math.min(l + PAGE, MAX))}
            >
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          ) : (
            <span>Showing the maximum {MAX} events per query. Narrow by date or actor to see older entries.</span>
          )}
        </div>
      )}
    </div>
  )
}
